import type { AsyncStatus, PhoneNumber } from 'lib-shared-legacy-types';
import { Action, handleActions } from 'redux-actions';


import { callFlowsActions } from '../actions/callFlowsActions';
import {
  DeleteFlowResponseT,
  FetchFlowsFromNumbersResponseT,
  FlowCallFlowT,
  GetQueryNumbersPageResponseT,
} from '../api/types';

export type CallFlowsStateT = {
  flows: FlowCallFlowT[];
  meta: {
    asyncStatus: AsyncStatus;
    callAsyncStatus: AsyncStatus;
  };
  nextPage?: null | string;
  numbers: PhoneNumber[];
  previousPage?: null | string;
};

type FlowCallHandleActionPayloadT =
  | FetchFlowsFromNumbersResponseT
  | DeleteFlowResponseT
  | GetQueryNumbersPageResponseT
  | any;

export const {
  callFlowsFetchAll,
  callFlowsFetchAllSucceeded,
  callFlowsFetchAllFailed,
  callFlowsDelete,
  callFlowsDeleteSucceeded,
  callFlowsDeleteFailed,
  telephonyGetQueryNumbersPage,
  telephonyGetQueryNumbersPageSucceeded,
  telephonyGetQueryNumbersPageFailed,
} = callFlowsActions;

const initialState: CallFlowsStateT = {
  flows: [],
  meta: {
    asyncStatus: 'succeeded',
    callAsyncStatus: 'succeeded',
  },
  nextPage: null,
  numbers: [],
  previousPage: null,
};

// Reducer
const callFlows = handleActions<CallFlowsStateT, FlowCallHandleActionPayloadT>(
  {
    [callFlowsFetchAll.toString()]: (
      state: CallFlowsStateT,
    ): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, callAsyncStatus: 'loading' } };
    },

    [callFlowsFetchAllSucceeded.toString()]: (
      state: CallFlowsStateT,
      action: Action<FetchFlowsFromNumbersResponseT>,
    ): CallFlowsStateT => {
      const flows =
        action.payload.data?.conversation_flows &&
        Array.isArray(action.payload.data.conversation_flows)
          ? action.payload.data.conversation_flows
          : [];

      return {
        ...state,
        meta: { ...state.meta, callAsyncStatus: 'succeeded' },
        flows: flows,
      };
    },

    [callFlowsFetchAllFailed.toString()]: (
      state: CallFlowsStateT,
    ): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, callAsyncStatus: 'failed' } };
    },

    [callFlowsDelete.toString()]: (state: CallFlowsStateT): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [callFlowsDeleteSucceeded.toString()]: (
      state: CallFlowsStateT,
      action: Action<DeleteFlowResponseT>,
    ): CallFlowsStateT => {
      const { id } = action.payload.request;
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        flows: state.flows.filter((flow: FlowCallFlowT) => flow.id !== id),
      };
    },

    [callFlowsDeleteFailed.toString()]: (
      state: CallFlowsStateT,
    ): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [telephonyGetQueryNumbersPage.toString()]: (
      state: CallFlowsStateT,
    ): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [telephonyGetQueryNumbersPageSucceeded.toString()]: (
      state: CallFlowsStateT,
      action: Action<GetQueryNumbersPageResponseT>,
    ): CallFlowsStateT => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        numbers:
          action.payload.data &&
          Array.isArray(action.payload.data.numbers) === true
            ? action.payload.data.numbers
            : [],
        nextPage: action.payload.meta ? action.payload.meta.next_page : null,
        previousPage: action.payload.meta
          ? action.payload.meta.prev_page
          : null,
      };
    },

    [telephonyGetQueryNumbersPageFailed.toString()]: (
      state: CallFlowsStateT,
    ): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    ['FLOW_DELETE_LOCAL']: (state: CallFlowsStateT): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    ['FLOW_DELETE_LOCAL_SUCCEEDED']: (
      state: CallFlowsStateT,
    ): CallFlowsStateT => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        flows: state.flows.filter((flow: any) => flow.id !== 'local'),
      };
    },

    ['FLOW_DELETE_LOCAL_FAILED']: (state: CallFlowsStateT): CallFlowsStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    ['TELEPHONY_UPDATE_NUMBER_SUCCEEDED']: (
      state: CallFlowsStateT,
      action: any,
    ): CallFlowsStateT => {
      let numbers: PhoneNumber[] = [...state.numbers];
      const updatedNumber = Object.assign({}, action.payload.data);
      const name = updatedNumber.name;
      const number = updatedNumber.e164_format;
      const record_outbound_calls = updatedNumber.record_outbound_calls;

      const foundNumber: undefined | PhoneNumber = numbers.find(
        ({ e164_format }: PhoneNumber) => e164_format === number,
      );

      if (foundNumber) {
        foundNumber.name = name; // update to new name.
        foundNumber.record_outbound_calls = record_outbound_calls;
        numbers = numbers.map((n: PhoneNumber) =>
          n.e164_format === number ? foundNumber : n,
        );
      }

      return { ...state, numbers };
    },

    ['TELEPHONY_BUY_GROUP_NUMBER_DELIVERED']: (
      state: CallFlowsStateT,
      action: any,
    ): CallFlowsStateT => {
      const number: PhoneNumber = Object.assign({}, action.payload.data);
      const numberIndex = state.numbers.findIndex(
        (n: PhoneNumber) => n.e164_format === number.e164_format,
      );

      if (numberIndex && numberIndex > -1) {
        return {
          ...state,
          numbers: [
            ...state.numbers.slice(0, numberIndex),
            Object.assign({}, state.numbers[numberIndex], number),
            ...state.numbers.slice(numberIndex + 1),
          ],
        };
      }
      return { ...state, numbers: [...state.numbers, number] };
    },

    ['TELEPHONY_RELEASE_NUMBER_SUCCEEDED']: (
      state: CallFlowsStateT,
      action: any,
    ): CallFlowsStateT => {
      const deletedNumber = Object.assign({}, action.number);
      return {
        ...state,
        numbers: state.numbers.filter(
          (n: PhoneNumber) => n.e164_format !== deletedNumber.e164_format,
        ),
      };
    },

    ['TELEPHONY_BUY_NUMBER']: (
      state: CallFlowsStateT,
      action: any,
    ): CallFlowsStateT => {
      // We need this check if the number we buynot is known (not already in our pool).
      if (action.payload.number?.e164_format) {
        const e164_format = action.payload.number.e164_format;
        const name = action.payload.name;
        const numberObj: PhoneNumber = {
          carrier: null,
          default: false,
          e164_format,
          incoming_functionality: false,
          incoming_verified: false,
          is_verified: false,
          name,
          number_type: null,
          outgoing_functionality: false,
          porting_date: null,
          record_outbound_calls: false,
          region: undefined,
        };
        return { ...state, numbers: [...state.numbers, numberObj] };
      }
      return state;
    },

    ['TELEPHONY_BUY_NUMBER_SUCCEEDED']: (
      state: CallFlowsStateT,
      action: any,
    ): CallFlowsStateT => {
      const number = Object.assign({}, action.payload.data);

      const numberIndex = state.numbers.findIndex(
        (n: PhoneNumber) => n.e164_format === number.e164_format,
      );

      if (numberIndex && numberIndex > -1) {
        return {
          ...state,
          numbers: [
            ...state.numbers.slice(0, numberIndex),
            Object.assign({}, state.numbers[numberIndex], number),
            ...state.numbers.slice(numberIndex + 1),
          ],
        };
      }
      return { ...state, numbers: [...state.numbers, number] };
    },

    ['TELEPHONY_BUY_NUMBER_FAILED']: (
      state: CallFlowsStateT,
      action: any,
    ): CallFlowsStateT => {
      const optimisticNumbersArray = state.numbers.slice();
      const numbers: PhoneNumber[] = action.requestPayload?.number
        ? optimisticNumbersArray.filter(
            (number: PhoneNumber) =>
              number.e164_format !== action.requestPayload.number.e164_format,
          )
        : [];

      if (action.requestPayload?.number) {
        return { ...state, numbers };
      }

      return state;
    },

    ['FLOW_ADD_AFTER_BUY_NUMBER_SUCCEEDED']: (
      state: CallFlowsStateT,
      action: any,
    ): CallFlowsStateT => {
      const addedFlow = Object.assign({}, action.flow);
      return { ...state, flows: [...state.flows, addedFlow] };
    },
  },
  initialState,
);

export default callFlows;
